import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import KanbanBoard from "components/kanbanBoard/KanbanBoard"
import { getProspects, updateCustomer, getCustomerScores } from "interfaces/customer";
import { getKanban } from "interfaces/kanban";
import { useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";
import Card from "components/card";
import CustomerPopUp from "../editCustomer/customerPopUp";
import EditInformationPU from "../editCustomer/components/pop-up/editInformationPU";
import { useNavigate } from "react-router-dom";
import ProspectToCustomerPopUp from "./component/prospectToCustomerPopUp";
import ProspectListView from "./component/ProspectListView";
import { MdViewColumn, MdTableRows } from "react-icons/md";
import CRMMenu from "../list-customers/components/CRMMenu";
import SearchIcon from "components/icons/SearchIcon";
import { getUsersLight } from "interfaces/user";
import CreateButton from "components/buttons/CreateButton";

const Kanban = () => {
    const [cards, setCards] = useState(null);
    const [view, setView] = useState<'kanban' | 'list'>('kanban');
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState<any[]>([]);
    const [assignedTo, setAssignedTo] = useState('');
    const [columns, setColumns] = useState<any[]>([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenProspect,
        onOpen: onOpenProspect,
        onClose: onCloseProspect
    } = useDisclosure();
    const [selectedCard, setSelectedCard] = useState(null);
    const [lastCardMoved, setLastCardMoved] = useState(null);
    const [popUpState, setPopUpState] = useState('page');
    const nav = useNavigate();

    const fetchCustomers = async () => {
        const [res, scores] = await Promise.all([getProspects(""), getCustomerScores()]);
        if (res) {
            const defaultCards: any = [];
            res.data.forEach((card: any, index: number) => {
                defaultCards.push({
                    id: index + 1,
                    columnId: card.status === "prospect" ? "qualified" : card.status,
                    content: card.company,
                    ...card,
                    score: scores[card._id],
                })
            })
            setCards(defaultCards)
        }
    };

    const editCustomer = async (customer: any) => {

        const data = {
            _id: customer._id,
            deliveryTourId: customer.deliveryTourId,
            deliveryAddress: customer.deliveryAddress,
            billingAddress: customer.billingAddress,
            billingEmail: customer.billingEmail,
            billingPeriod: customer.billingPeriod,
            contact: customer.contact,
            company: customer.company,
            customerId: customer.customerId,
            mail: customer.mail,
            notes: customer.notes,
            vatNumber: customer.vatNumber,
            siren: customer.siren,
            phone: customer.phone,
            paymentMethod: customer.paymentMethod,
            displayName: customer.displayName,
            tags: customer.tags,
            status: customer.status,
        };
        await updateCustomer(data);
        // if (res.data) setSaveStatus("saved");
        // else setSaveStatus("error");
    };

    const onColumnDrop = (card: any) => {
        card.status = card.columnId;
        setLastCardMoved(card);
        editCustomer(card);
    }

    const onDragEnd = () => {
        console.log("end", lastCardMoved);
        if (lastCardMoved && lastCardMoved.status === "active")
            onOpenProspect();
    }

    useEffect(() => {
        fetchCustomers();
        (async () => {
            const u = await getUsersLight();
            if (u) setUsers(u);
            const kanban = await getKanban();
            if (kanban) setColumns(kanban.columns);
        })();
    }, []);

    const daysInCurrentStage = (card: any): number | null => {
        const history = card.stageHistory;
        if (!Array.isArray(history) || history.length === 0) return null;
        const last = history[history.length - 1];
        if (!last?.enteredAt) return null;
        const diffMs = Date.now() - new Date(last.enteredAt).getTime();
        return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    };

    const averageDaysByColumn = useMemo(() => {
        if (!cards) return {} as Record<string, number>;
        const sums: Record<string, { total: number; count: number }> = {};
        (cards as any[]).forEach((card) => {
            const days = daysInCurrentStage(card);
            if (days === null) return;
            if (!sums[card.columnId]) sums[card.columnId] = { total: 0, count: 0 };
            sums[card.columnId].total += days;
            sums[card.columnId].count += 1;
        });
        const result: Record<string, number> = {};
        Object.entries(sums).forEach(([columnId, { total, count }]) => {
            result[columnId] = Math.round(total / count);
        });
        return result;
    }, [cards]);

    const filteredCards = useMemo(() => {
        if (!cards) return cards;
        let result = cards as any[];
        const query = search.trim().toLowerCase();
        if (query) {
            result = result.filter((card: any) =>
                [card.company, card.displayName, card.contact, card.mail, card.phone]
                    .some((field) => field && String(field).toLowerCase().includes(query))
            );
        }
        if (assignedTo) {
            result = result.filter((card: any) =>
                Array.isArray(card.assignedTo) && card.assignedTo.includes(assignedTo)
            );
        }
        return result;
    }, [cards, search, assignedTo]);

    return (
        <div className="relative">
            <ProspectToCustomerPopUp
                isOpen={isOpenProspect}
                onClose={onCloseProspect}
                onClick={() => {
                    nav(`/edit-customer/${lastCardMoved._id}`);
                    onCloseProspect();
                }}
            />
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="top-[22vh] !m-auto !w-max !max-w-[85%] md:top-[12vh]">
                    <ModalBody>
                        <Card extra="px-[30px] sm:ml-[168px] sm:mr-[9px] sm:pt-[35px] pb-[40px] xl:max-w-[80%] xl:max-w-7xl flex !z-[1004] xl:mx-auto max-h-[600px] overflow-y-auto">
                            {popUpState === "page" && (
                                <CustomerPopUp
                                    id={selectedCard}
                                    onPageChange={(page: string) =>
                                        setPopUpState(page)
                                    }
                                    onDeleted={() => {
                                        onClose();
                                        fetchCustomers();
                                    }}
                                />
                            )}
                            {popUpState === "edit" && (
                                <EditInformationPU
                                    id={selectedCard}
                                    onPageChange={(page: string) =>
                                        setPopUpState(page)
                                    }
                                />
                            )}
                        </Card>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <CRMMenu path="/kanban" />
                <div className="flex items-center gap-3 mr-5">
                    <div className="flex h-[38px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white w-[280px]">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Entreprise, mail, téléphone, contact, ...."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                        />
                    </div>
                    <div className="relative">
                        <select
                            value={assignedTo}
                            onChange={(e) => setAssignedTo(e.target.value)}
                            className="w-44 sm:w-56 max-w-[220px] rounded-md border border-gray-300 px-2 py-1 h-8 text-xs focus:border-brand-500 outline-none bg-white"
                            disabled={!users?.length}
                            title="Filtrer par commercial"
                        >
                            <option value="">Tous les commerciaux</option>
                            {users.map((u) => (
                                <option key={u._id} value={u._id} title={u.email || u.fullName}>
                                    {u.fullName} {u.email ? `(${u.email})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button
                            className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'kanban' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                            title="Vue kanban"
                            onClick={() => setView('kanban')}
                        >
                            <MdViewColumn />
                        </button>
                        <button
                            className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'list' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                            title="Vue liste"
                            onClick={() => setView('list')}
                        >
                            <MdTableRows />
                        </button>
                    </div>
                    <CreateButton
                        value="Importer CSV/XLSX"
                        extra="h-8 !py-0 !px-3 text-xs"
                        onClick={() => nav('/import-prospect')}
                    />
                </div>
            </div>
            {columns.length > 0 && Object.keys(averageDaysByColumn).length > 0 && (
                <div className="flex gap-4 flex-wrap mt-3 text-xs text-gray-500">
                    <span className="font-medium">Temps moyen dans l'étape :</span>
                    {columns.map((col: any) => (
                        averageDaysByColumn[col.id] !== undefined && (
                            <span key={col.id}>
                                {col.title} : <span className="font-semibold">{averageDaysByColumn[col.id]} j</span>
                            </span>
                        )
                    ))}
                </div>
            )}
            {filteredCards && view === 'kanban' && (
                <KanbanBoard
                    cards={filteredCards}
                    onColumnDrop={onColumnDrop}
                    onDragEnded={onDragEnd}
                    onCardClick={(id: string) => {
                        setSelectedCard(id);
                        setPopUpState("page");
                        onOpen();
                    }}
                />
            )}
            {filteredCards && view === 'list' && (
                <ProspectListView
                    cards={filteredCards}
                    users={users}
                    onCardClick={(id: string) => {
                        setSelectedCard(id);
                        setPopUpState("page");
                        onOpen();
                    }}
                />
            )}
        </div>
    );
}

export default Kanban;